import { ConnectKitButton } from "connectkit";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAccount } from "wagmi";
import Auth from "./Auth";
import { types } from "./lib/client";
import { EntityDetails, PrePurchaseFailureReason, SaleEligibility } from "./lib/types";
import useEcho from "./lib/useEcho";
import MegaSale from "./MegaSale";
import PlasmaSale from "./PlasmaSale";

enum SaleState {
    ENTITY_SELECTION,
    INELIGIBLE,
    NEEDS_LIVENESS_CHECK,
    NOT_READY_TO_PURCHASE,
    READY_TO_PURCHASE,
    ERROR,
}

const EntityRow = ({ value, onClick }: { value: types.EntityDetails; onClick: () => void }) => {
    return (
        <div
            className="flex flex-col gap-1 text-md font-medium w-full cursor-pointer outline outline-2 outline-white p-4 rounded-md"
            onClick={onClick}
        >
            <span className="text-gray-200">
                {value.Label} - {value.EntityUUID}
            </span>
            <span className="text-gray-300">Entity setup state: {value.EntitySetupState}</span>
            <span className="text-gray-300">Sale eligibility: {value.SaleEligibility}</span>
        </div>
    );
};

function App() {
    const { authenticated, logout, readEntity, prePurchaseCheck, sonarHomeURL } = useEcho();
    const { address: userAddress } = useAccount();

    const [livenessUrl, setLivenessUrl] = useState<string | null>(null);
    const [saleState, setSaleState] = useState<SaleState>(SaleState.ENTITY_SELECTION);
    const [entity, setEntity] = useState<EntityDetails | null>(null);
    const entitiesLoaded = useRef(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [prePurchaseFailureReason, setPrePurchaseFailureReason] = useState<PrePurchaseFailureReason | null>(null);

    const getEntity = useCallback(async () => {
        if (!authenticated || !userAddress) {
            return;
        }

        try {
            const entity = await readEntity(userAddress);
            setEntity(entity);
        } catch (error) {
            console.error("Failed to load entities:", error);
            setErrorMessage("Failed to load entities");
            setSaleState(SaleState.ERROR);
        }
    }, [authenticated, readEntity, userAddress]);

    const doPrePurchaseCheck = useCallback(async () => {
        if (!authenticated || !entity || !userAddress) {
            return;
        }

        const response = await prePurchaseCheck({
            entityUUID: entity.EntityUUID,
            entityType: entity.EntityType,
            walletAddress: userAddress,
        });
        if (response.ReadyToPurchase) {
            setSaleState(SaleState.READY_TO_PURCHASE);
            return;
        }

        setPrePurchaseFailureReason(response.FailureReason);
        if (response.FailureReason === PrePurchaseFailureReason.REQUIRES_LIVENESS && response.LivenessCheckURL) {
            setSaleState(SaleState.NEEDS_LIVENESS_CHECK);
            setLivenessUrl(response.LivenessCheckURL);
            return;
        }

        setSaleState(SaleState.NOT_READY_TO_PURCHASE);
    }, [authenticated, entity, prePurchaseCheck, userAddress]);

    const checkSaleEligibility = useCallback(async () => {
        if (!authenticated || !entity) {
            return;
        }

        if (entity.SaleEligibility === SaleEligibility.ELIGIBLE) {
            doPrePurchaseCheck();
            return;
        }
        setSaleState(SaleState.INELIGIBLE);
    }, [authenticated, entity, doPrePurchaseCheck]);

    const logoutAndRedirect = useCallback(() => {
        if (!logout) {
            return;
        }

        logout();
        window.location.href = "/";
    }, [logout]);

    // Load entities only once when the component mounts and client is available
    useEffect(() => {
        if (authenticated && userAddress && !entitiesLoaded.current) {
            getEntity();
            entitiesLoaded.current = true;
        }
    }, [authenticated, userAddress, getEntity]);

    useEffect(() => {
        checkSaleEligibility();
    }, [checkSaleEligibility]);

    if (!authenticated) {
        return <Auth />;
    }

    return (
        <div className="flex flex-col gap-5 p-5 max-w-[700px] justify-center">
            <div className="flex flex-col gap-4">
                <ConnectKitButton />
            </div>

            {userAddress && (
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-4">
                        {entity ? (
                            <EntityRow
                                key={entity.EntityUUID}
                                value={entity}
                                onClick={() => {
                                    if (entity.EntitySetupState !== "complete") {
                                        window.location.assign(sonarHomeURL);
                                        return;
                                    }
                                }}
                            />
                        ) : (
                            <div
                                className="flex flex-col gap-1 text-md font-medium w-full cursor-pointer outline outline-2 outline-white p-4 rounded-md"
                                onClick={() => window.location.assign(sonarHomeURL)}
                            >
                                <span className="text-gray-200">
                                    Link a wallet to your Sonar entity that matches the one you're currently connected
                                    to
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}
            <hr className="border-t border-gray-700" />
            {
                {
                    [SaleState.ENTITY_SELECTION]: null,
                    [SaleState.INELIGIBLE]: (
                        <div className="flex flex-col gap-4">
                            <p className="text-gray-200 text-lg font-bold">State: Ineligible</p>
                        </div>
                    ),
                    [SaleState.NOT_READY_TO_PURCHASE]: (
                        <div className="flex flex-col gap-4">
                            <p className="text-gray-200 text-lg font-bold">
                                PrePurchaseCheck Failure Reason: {prePurchaseFailureReason}
                            </p>
                        </div>
                    ),
                    [SaleState.NEEDS_LIVENESS_CHECK]: (
                        <div className="flex flex-col gap-4">
                            <p className="text-gray-200 text-lg font-bold">State: Needs Liveness Check</p>
                            <button
                                className="bg-white text-gray-800 rounded-md px-4 py-2 font-mono"
                                onClick={() => {
                                    if (livenessUrl) {
                                        window.location.assign(livenessUrl);
                                    }
                                }}
                            >
                                Start Liveness Check
                            </button>
                        </div>
                    ),
                    [SaleState.READY_TO_PURCHASE]: <Sale selectedEntity={entity!} />,
                    [SaleState.ERROR]: (
                        <div className="flex flex-col gap-4">
                            <p className="text-gray-200 text-lg font-bold">State: Error</p>
                            <p>{errorMessage}</p>
                        </div>
                    ),
                }[saleState]
            }
            <button onClick={logoutAndRedirect}>Logout</button>
        </div>
    );
}

enum SaleType {
    Plasma,
    MegaETH,
}

const Sale = ({ selectedEntity }: { selectedEntity: EntityDetails }) => {
    const [saleType, setSaleType] = useState<SaleType>(SaleType.MegaETH);
    return (
        <div className="flex flex-col gap-4">
            <div className="flex gap-2 mb-4">
                <button
                    className={`px-4 py-2 rounded-md font-mono ${
                        saleType === SaleType.MegaETH ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-800"
                    }`}
                    onClick={() => setSaleType(SaleType.MegaETH)}
                >
                    MegaETH
                </button>
                <button
                    className={`px-4 py-2 rounded-md font-mono ${
                        saleType === SaleType.Plasma ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-800"
                    }`}
                    onClick={() => setSaleType(SaleType.Plasma)}
                >
                    Plasma
                </button>
            </div>
            {saleType === SaleType.MegaETH ? (
                <MegaSale selectedEntity={selectedEntity} />
            ) : (
                <PlasmaSale selectedEntity={selectedEntity} />
            )}
        </div>
    );
};

export default App;
